import numeral from 'numeral';
import { ITransferMethod, ITransferMethodOptionsEntity } from './types';

// eslint-disable-next-line import/prefer-default-export
export const formatBondDate = (date: Date | string) => {
  if (!date) {
    return '-';
  }
  const formatFullMonthId = Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return formatFullMonthId.format(new Date(date));
};

export const formatBondCurrency = (
  value: number,
  signDisplay: 'never' | 'exceptZero' | 'auto' | 'always' = 'never',
  currency = 'IDR',
) =>
  Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    signDisplay,
    unitDisplay: 'narrow',
  })
    .format(value)
    .replace(/\s+/g, '');

export const formatPricePercentage = (
  value: number,
  signDisplay: 'never' | 'exceptZero' | 'auto' | 'always' = 'never',
) => {
  if (typeof value !== 'number') {
    return '-';
  }

  const rawValue = value / 100;

  return Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    signDisplay,
    unitDisplay: 'narrow',
  }).format(rawValue);
};

export const formatCurrency = (
  value: string,
  fractionDigits = 0,
  currency = 'Rp',
) =>
  `${currency}${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
  })}`;

export const formatShiftedCurrency = (
  value: number,
  fixZero = false,
  fractionDigits = 0,
  currency = 'Rp',
) => {
  const isNegative = value <= 0;
  const absoluteValue = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
  });

  const formattedValue = `${currency}${absoluteValue}`;

  if (fixZero && value === 0) return `${formattedValue}`;

  return `${isNegative ? '- ' : ''}${formattedValue}`;
};

export const formatPrice = (value: string, fractionDigits = 0) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
  });

export const formatNumber = (number) => numeral(number).format('0,0');

export const isOverDailyLimit = (
  inputValue: number,
  method: ITransferMethodOptionsEntity,
) => {
  const { code, amount, daily_limit } = method;
  const { remaining, max: dailyMax } = daily_limit || {};

  return (
    code &&
    remaining !== 0 &&
    dailyMax !== 0 &&
    remaining < amount.min &&
    (inputValue > remaining || inputValue > dailyMax)
  );
};

export const isUnderDailyLimit = (
  inputValue: number,
  method: ITransferMethodOptionsEntity,
) => {
  const { code, amount, daily_limit } = method;
  const { remaining, max: dailyMax } = daily_limit || {};

  return (
    code &&
    remaining !== 0 &&
    dailyMax !== 0 &&
    inputValue <= dailyMax &&
    remaining >= amount.min &&
    inputValue > remaining
  );
};

export const getMostMinMaxAmountRules = (
  methods: ITransferMethod['data']['transfer_options'],
) => {
  if (methods?.length) {
    const amounts = methods.map((method) => method.amount);
    const min = Math.min(...amounts.map((amount) => amount.min));
    const max = Math.max(...amounts.map((amount) => amount.max));
    return { min, max };
  }
  return { min: 0, max: 0 };
};

export const updateCursorPosition = (
  event: React.ChangeEvent<HTMLInputElement>,
  newCursorPosition: number,
) => {
  window.requestAnimationFrame(() => {
    const inputElement = event.target as HTMLInputElement;
    inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
  });
};

export const adjustCursorPosition = (
  event: React.ChangeEvent<HTMLInputElement>,
  cursorPosition: number,
  originalValue: string,
  formattedValue: string,
) => {
  const commaCountBefore =
    originalValue.slice(0, cursorPosition).split(',').length - 1;
  const commaCountAfter =
    formattedValue.slice(0, cursorPosition).split(',').length - 1;
  const adjustment = commaCountAfter - commaCountBefore;
  const newCursorPosition =
    formattedValue === '0' ? 1 : cursorPosition + adjustment;

  updateCursorPosition(event, newCursorPosition);
};
